import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faSquareInfo } from '@fortawesome/pro-regular-svg-icons';
import {
  faHexagonExclamation,
  faPartyHorn,
  faWarning,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import VoxelAlertIconButton from './VoxelSeverityIconButton';
import VoxelButton from '../Buttons/VoxelButton';
import VoxelIconButton from '../Buttons/VoxelIconButton';

export interface VoxelAlertProps {
  id?: string;
  title?: string;
  severity?: VoxelAlertSeverity;
  widthClass?: string;
  size?: 'xs' | 'sm' | 'md';
  fontAIconName?: IconProp;
  iconSize?: SizeProp;
  iconPosition?: 'top' | 'center' | 'title';
  skipIcon?: boolean;
  outlined?: boolean;
  onClose?: () => void;
  hideClose?: boolean;
  roundedClass?: string;
  onClick?: () => void;
  onClickTitle?: string;
}

export type VoxelAlertSeverity = 'neutral' | 'error' | 'warning' | 'info' | 'success';

export const VoxelAlert: React.FC<VoxelAlertProps> = ({
  id,
  severity = 'warning',
  title,
  children,
  widthClass = 'w-full',
  size = 'md',
  iconPosition = 'center',
  iconSize = '2x',
  fontAIconName,
  skipIcon = false,
  outlined,
  onClose,
  hideClose = true,
  roundedClass,
  onClick,
  onClickTitle,
}) => {
  let bgColor = '';
  let borderColor = '';
  let textColor = '';
  let icon: React.ReactNode;
  let topPadding = iconPosition === 'center' ? '' : 'mt-1';

  if (severity === 'error') {
    if (outlined) {
      borderColor = 'border border-error-700';
    } else {
      bgColor = 'bg-error-100/80';
    }
    textColor = 'text-error-700';
    icon = (
      <FontAwesomeIcon
        icon={fontAIconName ?? faHexagonExclamation}
        size={iconSize}
        className={`${textColor} ${topPadding}`}
      />
    );
  }
  if (severity === 'warning') {
    if (outlined) {
      borderColor = 'border border-warning-700';
    } else {
      bgColor = 'bg-warning-500/20';
    }
    textColor = 'text-warning-800';
    icon = (
      <FontAwesomeIcon
        icon={fontAIconName ?? faWarning}
        size={iconSize}
        className={`${textColor} ${topPadding}`}
      />
    );
  }
  if (severity === 'info') {
    if (outlined) {
      borderColor = 'border border-2  border-info-500';
    } else {
      bgColor = 'bg-info-600/10';
    }
    textColor = 'text-info-800';
    icon = (
      <FontAwesomeIcon
        icon={fontAIconName ?? faSquareInfo}
        size={iconSize}
        className={`${textColor} ${topPadding}`}
      />
    );
  }
  if (severity === 'success') {
    if (outlined) {
      borderColor = 'border border-2  border-success-100';
    } else {
      bgColor = 'bg-success-600/10';
    }
    textColor = 'text-success-900';
    icon = <FontAwesomeIcon
      icon={fontAIconName ?? faPartyHorn}
      size={iconSize}
      className={`${textColor} ${topPadding}`}
    />;
  }

  return (
    <div
      className={`flex flex-row 
        ${size === 'md' && 'gap-6 p-6'}
        ${size === 'sm' && 'gap-6 p-4 pl-6'}
        ${size === 'xs' && 'gap-3 p-3'}
        ${iconPosition === 'center' ? 'items-center' : 'items-start'}
        ${widthClass} 
        rounded-md ${roundedClass}
        ${bgColor} ${textColor} ${borderColor}
        ${!hideClose || !!onClickTitle?.length ? 'relative overflow-visible' : ''}
    `}
    >
      {/* Close button */}
      {!hideClose && size !== 'xs' && (
        <div className="absolute top-1/2 -translate-y-1/2  right-6">
          <VoxelAlertIconButton
            size="sm"
            color={severity}
            icon={<FontAwesomeIcon title={'Close'} icon={faXmark} size="lg" />}
            onClick={onClose}
            type="button"
          />
        </div>
      )}
      {/* Close button */}
      {!hideClose && size === 'xs' && (
        <div className="absolute -top-1.5 -right-1.5">
          <VoxelAlertIconButton
            size="xxxs"
            color={severity}
            icon={<FontAwesomeIcon title={'Close'} icon={faXmark} />}
            onClick={onClose}
            type="button"
            invertColors
          />
        </div>
      )}

      {!skipIcon && iconPosition !== 'title' && icon}
      <div className="flex-grow">
        {!!title?.length && (
          <div
            className={`font-sans pb-1 flex flex-row gap-4
            ${size === 'md' && 'text-base font-semibold'}
            ${size === 'sm' && 'text-sm font-semibold'}
            ${size === 'xs' && 'text-sm font-semibold'}
               `}
          >
            {!skipIcon && iconPosition === 'title' && icon} {title}
          </div>
        )}
        <span
          className={`font-sans font-normal text-sm whitespace-normal
            ${size === 'md' && 'text-sm'}
            ${size === 'sm' && 'text-[13px]'}
            ${size === 'xs' && 'text-xs'}
            `}
        >
          {children}
        </span>
      </div>
      {/* Other action button */}
      {!!onClickTitle?.length && (
        <div className="h-full mx-1">
          <div className="h-full flex flex-col justify-center ">
            <VoxelButton
              title={onClickTitle}
              size={size === 'xs' ? 'sm' : "md"}
              color={severity}
              type="button"
              onClick={onClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoxelAlert;
